import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Field, FieldGroup, Fieldset, Label } from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import {
  CheckReturn,
  StopPaymentCreate,
  StopPaymentReturn,
} from './clients/rust-api-client';
import { formatCurrency, formatDate, getBankErrorParts } from './utils/bankHelpers';
import { BANK_REASON_MAX_LEN, rangeCount, todayIso } from './utils/stopPaymentHelpers';

/**
 * Which stop is being placed.
 *
 * - `{ check }` — from the register: the check's number, date, amount and
 *   account are locked; only the reason and expiry are asked.
 * - `{ kind: 'check' }` — one check by number, for a check the register
 *   never saw.
 * - `{ kind: 'range' }` — a run of numbers, for a lost checkbook.
 */
export type StopPaymentMode =
  | { check: CheckReturn }
  | { kind: 'check' }
  | { kind: 'range' };

interface StopPaymentDialogProps {
  /** `null` closes the dialog. */
  mode: StopPaymentMode | null;
  hoaId: string;
  onClose: () => void;
  onPlaced: (after: StopPaymentReturn) => void;
}

interface AlertState {
  title: string;
  message: string;
}

interface AcctOption {
  id: string;
  accountNumber: string;
  purpose?: string | null;
}

const purposeLabel = (purpose?: string | null): string =>
  purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : 'Account';

/**
 * Place a stop payment. One dialog, three ways in.
 *
 * A stop tells the bank to refuse the check if it is presented. It is
 * reversible (release), so the confirm is a plain button — but the bank
 * may charge for it, and the dialog says so until Columbia names the
 * amount. The reason is for our records in full; the bank keeps its
 * first 20 characters, in capitals.
 */
const StopPaymentDialog: React.FC<StopPaymentDialogProps> = ({
  mode,
  hoaId,
  onClose,
  onPlaced,
}) => {
  const open = mode !== null;
  const fromCheck = mode && 'check' in mode ? mode.check : null;
  const kind: 'check' | 'range' = fromCheck
    ? 'check'
    : mode && 'kind' in mode
      ? mode.kind
      : 'check';

  const [accounts, setAccounts] = useState<AcctOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [accountId, setAccountId] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [checkDate, setCheckDate] = useState('');
  const [amount, setAmount] = useState('');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [reason, setReason] = useState('');
  const [expiration, setExpiration] = useState('');

  const [error, setError] = useState<AlertState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset on open; load the account picker only when the key is typed
  // by hand. The register path needs nothing from the server first.
  useEffect(() => {
    if (!open) return;
    setAccountId('');
    setCheckNumber('');
    setCheckDate('');
    setAmount('');
    setFirst('');
    setLast('');
    setReason('');
    setExpiration('');
    setError(null);
    setLoadError(null);
    if (fromCheck) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await bankService.listHoaAccounts({ hoaId });
        if (cancelled) return;
        const active = resp.data.accounts.filter((a) => a.is_active);
        setAccounts(
          active.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            purpose: a.purpose,
          }))
        );
        if (active.length === 1) setAccountId(active[0].id);
      } catch (err) {
        if (cancelled) return;
        const { body } = getBankErrorParts(err);
        setLoadError(body || "Couldn't load this HOA’s accounts.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `fromCheck` changes identity with `mode`; `open` covers it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hoaId]);

  const count = useMemo(() => rangeCount(first, last), [first, last]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const validate = (): StopPaymentCreate | null => {
    const trimmedReason = reason.trim();
    if (!trimmedReason) {
      setError({ title: 'Reason', message: 'Why is this check being stopped?' });
      return null;
    }
    if (expiration && expiration <= todayIso()) {
      setError({
        title: 'Expiration',
        message: 'The expiration must be a date after today, or blank for the bank’s default.',
      });
      return null;
    }
    const base: StopPaymentCreate = {
      reason: trimmedReason,
      expiration_date: expiration || null,
    };
    if (fromCheck) {
      return { ...base, check_id: fromCheck.id };
    }
    if (!accountId) {
      setError({ title: 'Choose an account', message: 'Which account is the check drawn on?' });
      return null;
    }
    if (kind === 'range') {
      if (!/^\d+$/.test(first.trim()) || !/^\d+$/.test(last.trim())) {
        setError({ title: 'Check numbers', message: 'Digits only, first and last.' });
        return null;
      }
      if (count === null) {
        setError({
          title: 'Check numbers',
          message: 'The first number must be below the last.',
        });
        return null;
      }
      return {
        ...base,
        hoa_bank_account_id: accountId,
        kind: 'range',
        first_check_number: first.trim(),
        last_check_number: last.trim(),
      };
    }
    if (!/^\d+$/.test(checkNumber.trim())) {
      setError({ title: 'Check number', message: 'Digits only, as printed on the check.' });
      return null;
    }
    if (!checkDate) {
      setError({ title: 'Check date', message: 'The date written on the check.' });
      return null;
    }
    if (amount.trim()) {
      const amt = Number(amount);
      if (isNaN(amt) || amt <= 0) {
        setError({ title: 'Amount', message: 'Leave it blank, or enter the amount on the check.' });
        return null;
      }
    }
    return {
      ...base,
      hoa_bank_account_id: accountId,
      kind: 'check',
      check_number: checkNumber.trim(),
      check_date: checkDate,
      amount: amount.trim() || null,
    };
  };

  const handleSubmit = async () => {
    setError(null);
    const body = validate();
    if (!body) return;
    try {
      setSubmitting(true);
      const resp = await bankService.placeHoaStopPayment({
        hoaId,
        stopPaymentCreate: body,
      });
      onPlaced(resp.data);
      onClose();
    } catch (err) {
      const { status, body: msg } = getBankErrorParts(err);
      setError({
        title:
          status === 409
            ? 'Already stopped'
            : status === 400
              ? 'Stop not accepted'
              : "Couldn't place the stop",
        message: msg || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const title = fromCheck
    ? `Stop check #${fromCheck.check_number}`
    : kind === 'range'
      ? 'Stop a range of checks'
      : 'Stop a check';

  const reasonOver = reason.trim().length > BANK_REASON_MAX_LEN;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogDescription>
        {fromCheck
          ? `${formatCurrency(Number(fromCheck.amount))} to ${fromCheck.payee_name}, dated ${formatDate(fromCheck.issue_date)}. The bank will refuse this check if it is presented.`
          : kind === 'range'
            ? 'The bank will refuse every check numbered in this range if it is presented — the lost-checkbook case.'
            : 'The bank will refuse this check if it is presented. For a check the register never saw.'}
      </DialogDescription>
      <DialogBody>
        {error && (
          <ErrorAlert className="mb-4" title={error.title} onDismiss={() => setError(null)}>
            {error.message}
          </ErrorAlert>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : loadError ? (
          <ErrorAlert title="Couldn't load">{loadError}</ErrorAlert>
        ) : !fromCheck && accounts.length === 0 ? (
          <Text>Assign a bank account to this HOA before stopping checks.</Text>
        ) : (
          <Fieldset>
            <FieldGroup>
              {fromCheck ? (
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>Account</Label>
                    <Text className="tabular-nums">{fromCheck.account_number}</Text>
                  </Field>
                  <Field>
                    <Label>Check</Label>
                    <Text className="tabular-nums">
                      #{fromCheck.check_number} · {formatDate(fromCheck.issue_date)} ·{' '}
                      {formatCurrency(Number(fromCheck.amount))}
                    </Text>
                  </Field>
                </div>
              ) : (
                <>
                  <Field>
                    <Label>Account</Label>
                    <FieldPicker
                      label="Account"
                      value={accountId}
                      onChange={setAccountId}
                      placeholder="Select an account"
                      options={accounts.map((a) => ({
                        value: a.id,
                        label: `${purposeLabel(a.purpose)} · ${a.accountNumber}`,
                      }))}
                    />
                  </Field>
                  {kind === 'range' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <Field>
                        <Label>First check number</Label>
                        <Input
                          name="first_check_number"
                          inputMode="numeric"
                          value={first}
                          onChange={(e) => setFirst(e.target.value)}
                        />
                      </Field>
                      <Field>
                        <Label>Last check number</Label>
                        <Input
                          name="last_check_number"
                          inputMode="numeric"
                          value={last}
                          onChange={(e) => setLast(e.target.value)}
                        />
                      </Field>
                      {count !== null && (
                        <Text className="col-span-2 text-xs">
                          {count} {count === 1 ? 'check' : 'checks'}
                        </Text>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <Field>
                        <Label>Check number</Label>
                        <Input
                          name="check_number"
                          inputMode="numeric"
                          value={checkNumber}
                          onChange={(e) => setCheckNumber(e.target.value)}
                        />
                      </Field>
                      <Field>
                        <Label>Check date</Label>
                        <Input
                          name="check_date"
                          type="date"
                          value={checkDate}
                          onChange={(e) => setCheckDate(e.target.value)}
                        />
                      </Field>
                      <Field>
                        <Label>Amount (optional)</Label>
                        <Input
                          name="amount"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
              <Field>
                <Label>Reason</Label>
                <Input
                  name="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. lost in the mail"
                />
                <Text className={`text-xs ${reasonOver ? 'font-medium' : ''}`}>
                  The bank keeps the first {BANK_REASON_MAX_LEN} characters
                  {reason.trim().length > 0 ? ` (${Math.min(reason.trim().length, BANK_REASON_MAX_LEN)}/${BANK_REASON_MAX_LEN})` : ''}
                  ; the full reason stays in our records.
                </Text>
              </Field>
              <Field>
                <Label>Expires (optional)</Label>
                <Input
                  name="expiration_date"
                  type="date"
                  value={expiration}
                  onChange={(e) => setExpiration(e.target.value)}
                />
                <Text className="text-xs">
                  Leave blank for 180 days from today, the bank’s default.
                </Text>
              </Field>
            </FieldGroup>
          </Fieldset>
        )}
        <Text className="mt-3 text-xs">
          The bank may charge a stop-payment fee. A stop can be released
          later; it does not void the check in the books.
        </Text>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="sky"
          onClick={handleSubmit}
          disabled={
            submitting || loading || !!loadError || (!fromCheck && accounts.length === 0)
          }
        >
          {submitting ? 'Placing…' : 'Place stop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StopPaymentDialog;
